import {mapiqApi} from '@lib/infrastructure';
import {storageKeys, systemDateFormat, withPayloadType} from '@lib/utils';
import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import parseISO from 'date-fns/parseISO';
import {AppThunkApiConfig} from '../../createStore';
import {
  WorkspaceReservationCountPerNodeByDate,
  GroupReservationInviteeSearchResult,
  WorkspaceReservation,
  WorkspaceReservationStatus,
  GroupWorkspaceReservation,
} from '../entities';
import {
  toGroupWorkspaceReservationRequestData,
  toWorkspaceReservation,
  toWorkspaceReservationDto,
} from './mappers/workspaceReservationMapper';
import {handleError} from '../../infrastructure';
import {LoadWorkspaceReservationsResult, WorkspaceReservationLocation} from './types';
import {getUser} from '../user';
import {loadWorkdays} from '../workday';
import {isFulfilled} from '../meetings';

import uniqBy from 'lodash/uniqBy';
import {format} from 'date-fns';

export const loadMyWorkspaceReservations = createAsyncThunk<
  LoadWorkspaceReservationsResult,
  {from: string; to: string; today: string},
  AppThunkApiConfig
>(
  'workspaceReservations/loadWorkspaceReservations',
  async ({from: fromDateString, to: toDateString, today: todayDateString}, {extra, getState}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const from = parseISO(fromDateString);
    const to = parseISO(toDateString);
    const today = parseISO(todayDateString);

    const [workspaceReservationsResponse, workspaceReservationCountPerNodeOnDateResponse] = await Promise.all([
      api.getWorkspaceReservations(from, to),
      api.getWorkspaceReservationCountPerNode(today, to),
    ]);

    const user = getState().data.user.user;

    const workspaceReservations = workspaceReservationsResponse.map((workspaceReservation) =>
      toWorkspaceReservation(workspaceReservation, user.id),
    );
    const workspaceReservationCountPerNodeOnDate: WorkspaceReservationCountPerNodeByDate = {
      ...workspaceReservationCountPerNodeOnDateResponse,
    } as WorkspaceReservationCountPerNodeByDate;

    return {
      workspaceReservations: workspaceReservations,
      workspaceReservationCountPerNodeOnDate: workspaceReservationCountPerNodeOnDate,
    };
  },
);

export const registerWorkspaceReservation = createAsyncThunk<
  WorkspaceReservation,
  {nodeIds: string[]; selectedDate: string},
  AppThunkApiConfig
>('workspaceReservations/registerWorkspaceReservation', async ({nodeIds, selectedDate}, {extra, getState}) => {
  const client = extra.apiClient;
  const api = mapiqApi(client);
  const user = getState().data.user.user;

  const data = toWorkspaceReservationDto(user.id, nodeIds, selectedDate);
  const response = await api.postWorkspaceReservation(data);
  return toWorkspaceReservation(response, user.id);
});

export const registerWorkspaceReservationWithPreferredLocation = createAsyncThunk<
  WorkspaceReservation,
  {selectedDate: string},
  AppThunkApiConfig
>(
  'workspaceReservations/registerWorkspaceReservationWithPreferredLocation',
  async ({selectedDate}, {extra, getState, dispatch, rejectWithValue}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const user = getState().data.user.user;

    try {
      const response = await api.postWorkspaceReservationWithPreferredLocation({
        startDate: format(parseISO(selectedDate), systemDateFormat),
        endDate: format(parseISO(selectedDate), systemDateFormat),
      });

      await dispatch(loadWorkdays({from: selectedDate, to: selectedDate}));

      return toWorkspaceReservation(response, user.id);
    } catch (error) {
      throw rejectWithValue(error);
    }
  },
);

export const updateWorkspaceReservation = createAsyncThunk<
  WorkspaceReservation,
  {
    nodeIds: string[];
    selectedDate: string;
    workspaceReservationId: string;
    invitees?: GroupReservationInviteeSearchResult[];
  },
  AppThunkApiConfig
>(
  'workspaceReservations/updateWorkspaceReservation',
  async ({nodeIds, selectedDate, invitees, workspaceReservationId}, {extra, getState, rejectWithValue}) => {
    try {
      const client = extra.apiClient;
      const api = mapiqApi(client);
      const user = getState().data.user.user;

      const data = toWorkspaceReservationDto(user.id, nodeIds, selectedDate, workspaceReservationId, invitees);
      const response = await api.patchWorkspaceReservation(data);
      return toWorkspaceReservation(response, user.id);
    } catch (error) {
      throw rejectWithValue(error);
    }
  },
);

export const removeWorkspaceReservation = createAsyncThunk<string, WorkspaceReservation, AppThunkApiConfig>(
  'workspaceReservations/removeWorkspaceReservation',
  async (workspaceReservation, {extra, getState, dispatch}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const user = getState().data.user.user;

    await api.deleteWorkspaceReservation(workspaceReservation.id);

    return user.id;
  },
);

export const loadLastSelectedWorkspaceReservationLocation = createAsyncThunk<
  WorkspaceReservationLocation | null,
  void,
  AppThunkApiConfig
>('workspaceReservations/loadLastSelectedWorkspaceReservationLocation', async (_, {extra}) => {
  const {getItem} = extra.localStore;
  const lastSelectedLocationString = await getItem(storageKeys.current.lastSelectedWorkspaceReservationLocation);
  const location = lastSelectedLocationString
    ? <WorkspaceReservationLocation>JSON.parse(lastSelectedLocationString)
    : null;
  return location;
});

export const saveLastSelectedWorkspaceReservationLocation = createAsyncThunk<
  WorkspaceReservationLocation,
  Partial<WorkspaceReservationLocation>,
  AppThunkApiConfig
>('workspaceReservations/saveLastSelectedWorkspaceReservationLocation', async (location, {extra, rejectWithValue}) => {
  const {setItem} = extra.localStore;
  if (!location) {
    return rejectWithValue('Failed saving last selected location');
  }

  const {buildingId, floorId, areaId, deskId} = location;

  const lastSelected: WorkspaceReservationLocation = {
    buildingId: buildingId ?? '',
    floorId: floorId ?? '',
    areaId: areaId ?? '',
    deskId: deskId ?? '',
  };

  await setItem(storageKeys.current.lastSelectedWorkspaceReservationLocation, JSON.stringify(lastSelected));

  return lastSelected;
});

export const searchGroupWorkspaceInvitees = createAsyncThunk<
  GroupReservationInviteeSearchResult[],
  string,
  AppThunkApiConfig
>('workspaceReservations/searchInvitees', async (searchTerm, {extra}) => {
  const trimmedSearch = searchTerm.trim();
  if (trimmedSearch.length < 2) {
    return [];
  }

  const client = extra.apiClient;
  const api = mapiqApi(client);
  const result = await api.getPeople(searchTerm);
  return result;
});

export const searchMultipleGroupWorkspaceInvitees = createAsyncThunk<
  GroupReservationInviteeSearchResult[],
  string[],
  AppThunkApiConfig
>('workspaceReservations/searchMultipleInvitees', async (searchTerms, {extra, dispatch, rejectWithValue}) => {
  const trimmedSearchTerms = searchTerms
    .map((searchTerm) => searchTerm.trim())
    .filter((searchTerm) => searchTerm.length >= 2);

  if (!trimmedSearchTerms.length) return [];

  const client = extra.apiClient;
  const api = mapiqApi(client);

  try {
    const apiResult = await Promise.allSettled(trimmedSearchTerms.map((searchTerm) => api.getPeople(searchTerm)));
    const filteredResult = apiResult.filter(isFulfilled).flatMap(({value}) => value);

    return uniqBy(filteredResult, 'id') as GroupReservationInviteeSearchResult[];
  } catch (error) {
    handleError(error, dispatch);
    return rejectWithValue([]);
  }
});

export const loadGroupWorkspaceReservations = createAsyncThunk<
  GroupWorkspaceReservation[],
  {from: string; to: string; today: string},
  AppThunkApiConfig
>('workspaceReservation/loadGroupWorkspaceReservations', async ({from, to}, {extra}) => {
  const fromDate = parseISO(from);
  const toDate = parseISO(to);
  const client = extra.apiClient;
  const api = mapiqApi(client);

  const response = await api.getGroupWorkspaceReservations(fromDate, toDate);

  const groupWorkspaceReservations = response.map((groupWorkspaceReservation) => ({
    ...groupWorkspaceReservation,
    isUpdating: false,
  }));

  return groupWorkspaceReservations;
});

export const updateWorkspaceReservationStatus = createAsyncThunk<
  {groupReservationId: string; reservationId: string; status: WorkspaceReservationStatus; userId: string},
  {
    groupReservationId: string;
    reservationId: string;
    reservationStatus: WorkspaceReservationStatus;
    currentWorkspaceReservationId?: string;
    selectedDate: string;
  },
  AppThunkApiConfig
>(
  'workspaceReservation/updateGroupWorkspaceReservationStatus',
  async ({groupReservationId, reservationId, reservationStatus, selectedDate}, {extra, dispatch}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);

    const result = await api.putGroupWorkspaceReservationStatus(groupReservationId, reservationId, reservationStatus);

    if (reservationStatus === 'Confirmed') {
      await dispatch(loadWorkdays({from: selectedDate, to: selectedDate}));
    }

    return {
      ...result,
      groupReservationId,
    };
  },
);

export const loadWorkspaceReservationById = createAsyncThunk<WorkspaceReservation, string, AppThunkApiConfig>(
  'workspaceReservation/loadWorkspaceReservationById',
  async (resevationId, {extra, getState}) => {
    const user = getState().data.user.user;
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const result = await api.getWorkspaceReservation(resevationId);
    return toWorkspaceReservation(result, user.id);
  },
);

export const createGroupWorkspaceReservation = createAsyncThunk<
  GroupWorkspaceReservation,
  {
    nodeIds: string[];
    selectedDate: string;
    invitees: GroupReservationInviteeSearchResult[];
  },
  AppThunkApiConfig
>(
  'workspaceReservation/createGroupWorkspaceReservation',
  async ({invitees, nodeIds, selectedDate}, {extra, getState, dispatch, rejectWithValue}) => {
    try {
      const client = extra.apiClient;
      const api = mapiqApi(client);
      const user = getUser(getState());

      const requestData = toGroupWorkspaceReservationRequestData(nodeIds, user.id, invitees, selectedDate);
      const result = await api.postGroupWorkspaceReservation(requestData);
      const userReservationId = result.owner.reservationId;
      await dispatch(loadWorkspaceReservationById(userReservationId)).unwrap();

      return {...result, isUpdating: false};
    } catch (error) {
      throw rejectWithValue(error);
    }
  },
);

export const cancelWorkspaceReservation = createAction(
  'workspaceReservation/workspaceReservationCanceled',
  withPayloadType<string>(),
);
